/**
 * Компонент статуса MAX Client
 */
import { useEffect, useState } from 'react';
import { Badge, Button, Card, CardHeader, CardTitle, CardContent, ConfirmDialog } from '@/components/ui';
import { checkAuthStatus, disconnect, type AuthStatus } from '@/api/maxClient';
import { CheckCircle2, XCircle, AlertCircle, Loader2 } from 'lucide-react';
import { usePermissions } from '@/hooks/usePermissions';
import { useToast } from '@/hooks/useToast';

interface MaxClientStatusProps {
  onDisconnect?: () => void;
  tenantId?: string;
}

export function MaxClientStatus({ onDisconnect, tenantId }: MaxClientStatusProps) {
  const [status, setStatus] = useState<AuthStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [disconnecting, setDisconnecting] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const { can } = usePermissions();
  const { showError, showSuccess } = useToast();

  const canManage = can('telegram_client:manage') || can('settings:write');

  const loadStatus = async () => {
    if (!tenantId) {
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      const currentStatus = await checkAuthStatus(tenantId);
      setStatus(currentStatus);
    } catch (err) {
      console.error('Failed to load MAX status:', err);
      setStatus({ status: 'disconnected' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadStatus();
    if (!tenantId) return;
    const interval = setInterval(loadStatus, 30000);
    return () => clearInterval(interval);
  }, [tenantId]);

  const handleDisconnect = async () => {
    setShowConfirmDialog(false);
    try {
      setDisconnecting(true);
      await disconnect(tenantId);
      await loadStatus();
      onDisconnect?.();
      showSuccess('MAX аккаунт успешно отключен');
    } catch (err: any) {
      showError(err.response?.data?.error || 'Не удалось отключить MAX аккаунт');
    } finally {
      setDisconnecting(false);
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center p-6">
          <Loader2 className="w-5 h-5 animate-spin text-neutral-400" />
        </CardContent>
      </Card>
    );
  }

  const getStatusBadge = () => {
    switch (status?.status) {
      case 'connected':
        return (
          <Badge variant="success" className="flex items-center gap-1">
            <CheckCircle2 className="w-3 h-3" />
            Подключен
          </Badge>
        );
      case 'scanning':
        return (
          <Badge variant="warning" className="flex items-center gap-1">
            <AlertCircle className="w-3 h-3" />
            Ожидание QR
          </Badge>
        );
      case 'auth_required':
        return (
          <Badge variant="warning" className="flex items-center gap-1">
            <AlertCircle className="w-3 h-3" />
            Требуется авторизация
          </Badge>
        );
      default:
        return (
          <Badge variant="error" className="flex items-center gap-1">
            <XCircle className="w-3 h-3" />
            Не подключен
          </Badge>
        );
    }
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle>MAX Аккаунт (Client)</CardTitle>
          {getStatusBadge()}
        </div>
      </CardHeader>
      <CardContent>
        {status?.status === 'connected' && (
          <div className="space-y-3">
            {status.phone && (
              <p className="text-sm text-neutral-600">
                <span className="font-medium">Телефон:</span> {status.phone}
              </p>
            )}
            {status.user_id && (
              <p className="text-sm text-neutral-600">
                <span className="font-medium">User ID:</span> {status.user_id}
              </p>
            )}
            {typeof status.messages_count === 'number' && (
              <p className="text-sm text-neutral-600">
                <span className="font-medium">Всего сообщений:</span>{' '}
                <span className="text-violet-600 font-semibold">
                  {status.messages_count.toLocaleString('ru-RU')}
                </span>
              </p>
            )}
            {canManage && (
              <div className="pt-2">
                <Button
                  onClick={() => setShowConfirmDialog(true)}
                  variant="danger"
                  size="sm"
                  isLoading={disconnecting}
                  disabled={disconnecting}
                >
                  Отключить аккаунт
                </Button>
              </div>
            )}
          </div>
        )}
        {(status?.status === 'auth_required' || status?.status === 'scanning') && (
          <p className="text-sm text-neutral-600">
            Подключите MAX аккаунт клиники через QR-код для переписки как обычный клиент.
          </p>
        )}
        {status?.status === 'disconnected' && (
          <p className="text-sm text-neutral-600">MAX аккаунт не подключен.</p>
        )}
      </CardContent>
      <ConfirmDialog
        isOpen={showConfirmDialog}
        onClose={() => setShowConfirmDialog(false)}
        onConfirm={handleDisconnect}
        title="Отключение MAX аккаунта"
        message="Вы уверены, что хотите отключить MAX аккаунт?"
        confirmText="Отключить"
        cancelText="Отмена"
        variant="danger"
        isLoading={disconnecting}
      />
    </Card>
  );
}
